import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TicketPicker = ({ ticket, setTicket }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (ticket || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/customer-orders`, { params: { q: q.trim() } });
        setResults((r.data.orders || []).slice(0, 8));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, ticket]);

  if (ticket) {
    return (
      <div className="flex items-center justify-between rounded-md border border-purple-300 bg-purple-50 px-3 py-2 text-sm" data-testid="direct-line-ticket-linked">
        <span><ClipboardList className="mr-1 inline h-4 w-4" />{ticket.ticket_no} · {ticket.customer_name}</span>
        <button type="button" onClick={() => setTicket(null)} className="text-gray-500 hover:text-red-600" data-testid="direct-line-ticket-unlink"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CO ticket or customer (optional)" className="no-capitalize" autoCapitalize={false} data-testid="direct-line-ticket-search" />
      {results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg max-h-48 overflow-auto">
          {results.map((o) => (
            <button type="button" key={o.id} onClick={() => setTicket(o)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100" data-testid={`direct-line-ticket-option-${o.ticket_no}`}>
              <span className="font-mono text-xs text-gray-500">{o.ticket_no}</span> <span className="font-medium">{o.customer_name}</span>
              <span className="ml-1 text-xs text-gray-500">{o.items.map((i) => i.description).join(", ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const DirectOrderLineDialog = ({ product, onClose, onAdd }) => {
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("");
  const [ticket, setTicket] = useState(null);
  const descRef = useRef(null);

  useEffect(() => {
    if (!product) return;
    setDescription(""); setQty("1"); setCost(""); setTicket(null);
    setTimeout(() => descRef.current?.focus(), 100);
  }, [product]);

  if (!product) return null;

  const submit = (e) => {
    e?.preventDefault();
    const q = parseFloat(qty), c = parseFloat(cost);
    if (!description.trim()) return toast.error("Enter a description");
    if (!q || q <= 0) return toast.error("Enter a valid quantity");
    if (isNaN(c) || c < 0) return toast.error("Enter a valid price excl VAT");
    onAdd({ description: description.trim(), qty: q, unit_cost: c, ticket });
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="direct-order-line-dialog">
        <DialogHeader>
          <DialogTitle>{product.plu_code} · {product.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-sm">Description *</Label>
            <Input ref={descRef} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Hunter PGP sprinkler" data-testid="direct-line-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Qty *</Label>
              <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="direct-line-qty-input" />
            </div>
            <div>
              <Label className="text-sm">Price excl VAT (R) *</Label>
              <Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" data-testid="direct-line-cost-input" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Link to Customer Order ticket</Label>
            <TicketPicker ticket={ticket} setTicket={setTicket} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="direct-line-cancel-btn">Cancel</Button>
            <Button type="submit" data-testid="direct-line-add-btn">Add Line</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
